import { Component, EventEmitter, inject, Input, NgZone, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { DoneAuditingComponent } from '@components/done-auditing/done-auditing.component';
import { FlagPhotoComponent } from '@components/flag-photo/flag-photo.component';
import { RecordResponse } from '@models/server';
import { Cable } from '@services/cable';
import { OverlayService } from '@services/overlay';
import { Subscription, take } from 'rxjs';
import { CommonModule, NgClass, DatePipe } from '@angular/common';
import { IconDirective } from '../../directives/icon.directive';
import { TranslateModule } from '@ngx-translate/core';
import { NgxPaginationModule } from 'ngx-pagination';
import { AttendancePhoto, Session, VerificationServiceEnum } from 'src/app/queries/generated';
import { VerificationsService } from '@services/verifications.service';
import { AttendancePhotosService } from '@services/attendance-photos.service';

import * as mousetrap from 'mousetrap';
import * as ActionCable from '@rails/actioncable';

interface SessionChannelData {
  attendancePhoto?: RecordResponse;
  lastId: string;
}

@Component({
  selector: 'lv-agent-verify-attendance',
  templateUrl: './agent-verify-attendance.component.html',
  styleUrls: ['./agent-verify-attendance.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, IconDirective, TranslateModule, NgClass, NgxPaginationModule, DatePipe]
})
export class AgentVerifyAttendanceComponent implements OnInit, OnDestroy {
  private verificationsService = inject(VerificationsService);
  private attendancePhotosService = inject(AttendancePhotosService);
  private overlayService = inject(OverlayService);

  private router = inject(Router);
  private cable = inject(Cable);
  private zone = inject(NgZone);

  private isFlagging = false;

  @Input() session: Session;
  @Input() passed: EventEmitter<boolean>;
  @Input() failed: EventEmitter<string>;

  attendancePhotos: AttendancePhoto[] = [];
  channel: ActionCable.Channel;
  passedSubscription: Subscription;
  failedSubscription: Subscription;
  selectedIndex = 0;
  perPage = 6;
  page = 1;

  get selectedPhoto(): any {
    return this.attendancePhotos[this.selectedIndex];
  }

  get photoNumber(): number {
    return this.selectedIndex + 1;
  }

  get photoCount(): number {
    return this.attendancePhotos.length;
  }

  get hasAttendancePhotos(): boolean {
    return this.photoCount > 0;
  }

  isFirstPhoto(): boolean {
    return this.selectedIndex === 0;
  }

  isLastPhoto(): boolean {
    return this.selectedIndex === this.attendancePhotos.length - 1;
  }

  ngOnInit() {
    if (this.session) {
      /// Initialize attendance photos from session data
      this.attendancePhotos = this.session.attendancePhotos.nodes || [];
    }

    this.passedSubscription = this.passed.asObservable().subscribe(() => this.passSession());
    this.failedSubscription = this.failed.asObservable().subscribe((reason) => this.failSession(reason));

    if (!this.session.finished) {
      this.connectToChannel();
    }

    this.bindKeys();
  }

  ngOnDestroy() {
    this.passedSubscription.unsubscribe();
    this.failedSubscription.unsubscribe();
    if (this.channel) {
      this.channel.unsubscribe();
    }

    mousetrap.unbind('f');
    mousetrap.unbind('right');
    mousetrap.unbind('left');
  }

  passSession() {
    this.verificationsService.pass(this.session.id, VerificationServiceEnum.Attendance).subscribe({
      next: () => {
        this.overlayService.open(DoneAuditingComponent, {
          session: this.session,
          passOrFail: 'pass'
        });
        this.goHome();
      },
      error: (error) => console.error('error passing session', error)
    });
  }

  failSession(reason: string) {
    this.verificationsService.fail(this.session.id, VerificationServiceEnum.Attendance, reason).subscribe({
      next: () => {
        this.overlayService.open(DoneAuditingComponent, {
          session: this.session,
          passOrFail: 'fail'
        });
        this.goHome();
      },
      error: (error) => console.error('error failing session', error)
    });
  }

  next() {
    this.selectedIndex = Math.min(this.selectedIndex + 1, this.attendancePhotos.length - 1);
    const newPage = Math.floor(this.selectedIndex / this.perPage) + 1;
    if (newPage !== this.page) {
      this.page = newPage;
    }
  }

  previous() {
    this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
    const newPage = Math.floor(this.selectedIndex / this.perPage) + 1;
    if (newPage !== this.page) {
      this.page = newPage;
    }
  }

  select(index: number) {
    this.selectedIndex = (this.page - 1) * this.perPage + index;
  }

  classFor(index: number) {
    index = index % this.perPage; // RM: occasionally got back actual index
    const pagedIndex = (this.page - 1) * this.perPage + index;

    return {
      'is-selected': this.selectedIndex === pagedIndex,
      'is-flagged': this.isFlagged(pagedIndex)
    };
  }

  isFlagged(index: number): boolean {
    const photo = this.attendancePhotos[index];
    if (photo) {
      return photo.flagged;
    }
    return false;
  }

  flag(photo: AttendancePhoto) {
    this.isFlagging = true;

    this.overlayService
      .open(FlagPhotoComponent, { photo: photo, session: this.session })
      .close.subscribe(() => (this.isFlagging = false));
  }

  private goHome() {
    // Add a small delay to ensure token refresh is completed before navigation
    setTimeout(() => this.router.navigateByUrl('/'), 100);
  }

  private connectToChannel() {
    const cableParams = {
      channel: 'SessionChannel',
      token: this.session.token
    };

    /// When a learner is taking a course with online ID, the photos are sent here.
    this.channel = this.cable.create(cableParams, (data: SessionChannelData) => {
      if (data.attendancePhoto) {
        this.handleChannelData(data);
      }
    });
  }

  private bindKeys() {
    // Key binding
    mousetrap.bind('f', () => {
      this.zone.run(() => {
        if (!this.isFlagging) {
          this.flag(this.selectedPhoto);
        }
      });
    });
    mousetrap.bind('right', () => {
      this.zone.run(() => {
        if (!this.isFlagging) {
          this.next();
        }
      });
    });
    mousetrap.bind('left', () => {
      this.zone.run(() => {
        if (!this.isFlagging) {
          this.previous();
        }
      });
    });
  }

  private handleChannelData(data: SessionChannelData) {
    const photoId = data.attendancePhoto.data.attributes.gid;

    /// Find the new photo and add it to the array
    this.attendancePhotosService
      .find(photoId)
      .pipe(take(1))
      .subscribe({
        next: (photo: AttendancePhoto) => this.handleNewAttendancePhoto(photo, data.lastId),
        error: (error) => console.error('error finding attendance photo', error)
      });
  }

  private handleNewAttendancePhoto(photo: AttendancePhoto, lastId: string) {
    const lastPhoto = this.attendancePhotos[this.attendancePhotos.length - 1];

    if (!lastPhoto || lastPhoto.id === lastId) {
      // We have everything, just append.
      this.attendancePhotos = [...this.attendancePhotos, photo];
    } else {
      // We're missing something, reload.
      this.reloadAttendancePhotos();
    }
  }

  private reloadAttendancePhotos() {
    this.attendancePhotosService
      .forSession(this.session.id)
      .pipe(take(1))
      .subscribe({
        next: (photos: AttendancePhoto[]) => (this.attendancePhotos = photos),
        error: (error) => console.error('error fetching attendance photos', error)
      });
  }
}
